import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import {
  createCrmOrderCustomerAddress,
  fetchCrmOrderDocumentOptions,
  getFriendlyApiError,
  isInternalApiError,
  previewCrmOrderTransition,
  RETRYING_CONNECTIVITY_MESSAGE,
  retryTransientRequest,
  transitionCrmOrder,
} from "../../../../../services/crm";

const currencyOptions = ["USD", "EUR", "GBP", "CAD", "INR"];
const twoColumnGridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
  gap: 1.5,
  width: "100%",
};
const truncatedSelectSx = {
  minWidth: 0,
  width: "100%",
  "& .MuiSelect-select": {
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};
const emptyAddressDraft = {
  addressName: "",
  companyName: "",
  streetAddress: "",
  suiteApt: "",
  city: "",
  stateProvince: "",
  postalZipCode: "",
  country: "Canada",
};

const today = () => new Date().toISOString().slice(0, 10);

const dateValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const documentConfig = {
  presentation: {
    title: "Create Presentation Draft",
    defaultTitle: "Presentation Draft",
    helperText: "Presentation sent status is managed through the send workflow.",
  },
  estimate: {
    title: "Create Estimate Draft",
    defaultTitle: "Estimate Draft",
    helperText: "This creates a draft estimate only. It will not be sent to the customer.",
  },
  salesOrder: {
    title: "Create Agreement Pending",
    defaultTitle: "Agreement Pending",
    helperText: "This creates a pending sales order/agreement only. It will not be sent to the customer.",
  },
};

const openInNewTab = (route) => {
  if (!route || route === "#") return false;
  window.open(route, "_blank", "noopener,noreferrer");
  return true;
};

const asArray = (value) => (Array.isArray(value) ? value : []);

const normalizeOptions = (response) => ({
  terms: asArray(response?.options?.terms).map((option) => ({
    ...option,
    value: String(option.id),
  })),
  taxes: asArray(response?.options?.taxes).map((option) => ({
    ...option,
    value: String(option.id),
  })),
  currencies: asArray(response?.options?.currencies).map((option) => ({
    ...option,
    value: String(option.id),
  })),
  contacts: asArray(response?.options?.contacts).map((option) => ({
    ...option,
    value: String(option.id),
  })),
  billingAddresses: asArray(response?.options?.billingAddresses).map((option) => ({
    ...option,
    value: String(option.id),
  })),
  shippingAddresses: asArray(response?.options?.shippingAddresses).map((option) => ({
    ...option,
    value: String(option.id),
  })),
});

const buildDefaults = ({ card, response, documentType, options }) => {
  const defaults = response?.defaults || {};
  const policy = response?.policySummary || {};
  const projectName =
    response?.project?.projectName ||
    card?.projectName ||
    `Project #${card?.orderId || ""}`;

  const billingAddressId = defaults.billingAddressId || "";
  const billingContactId = defaults.billingContactId || "";
  const shippingAddressId = defaults.shippingAddressId || "";
  const shippingContactId = defaults.shippingContactId || "";
  const shippingSameAsBilling =
    Boolean(billingAddressId || billingContactId) &&
    billingAddressId === shippingAddressId &&
    billingContactId === shippingContactId;

  return {
    documentTitle:
      defaults.documentTitle ||
      `${projectName} - ${documentConfig[documentType]?.defaultTitle || "Document"}`,
    commentsEnabled: defaults.commentsMode !== false,
    orderDate: dateValue(defaults.orderDate) || today(),
    terms: defaults.termsId ? String(defaults.termsId) : "",
    currency: defaults.currency || "CAD",
    inhandsDate: dateValue(defaults.inHandsDate) || "",
    defaultTax: defaults.taxId ? String(defaults.taxId) : "",
    billingAddressId,
    billingAddress:
      options.billingAddresses.find((option) => option.value === defaults.billingAddressId)?.display || "",
    billingContactId,
    billingContact:
      options.contacts.find((option) => option.value === defaults.billingContactId)?.label || "",
    shippingAddressId,
    shippingAddress:
      options.shippingAddresses.find((option) => option.value === defaults.shippingAddressId)?.display || "",
    shippingContactId,
    shippingContact:
      options.contacts.find((option) => option.value === defaults.shippingContactId)?.label || "",
    clientContactId: defaults.clientContactId || "",
    clientContactEmail: defaults.clientContactEmail || "",
    presentationDate: today(),
    hidePricing: false,
    requireUpfrontPayment: policy.requireUpfrontPayment === true,
    upfrontMode: policy.upfrontMode || null,
    depositType: policy.depositType || null,
    depositValue: policy.depositValue ?? "",
    paymentDueOnSign: policy.paymentDueOnSign ?? null,
    shippingSameAsBilling,
  };
};

export default function FastUpdateDialog({
  open,
  instanceUniqueId,
  transition,
  onClose,
  onSuccess,
  onConflict,
  onOpenCart,
}) {
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [retryNotice, setRetryNotice] = useState("");
  const [values, setValues] = useState({});
  const [confirmation, setConfirmation] = useState(null);
  const [createNewConfirmed, setCreateNewConfirmed] = useState(false);
  const [previewReady, setPreviewReady] = useState(false);
  const [hasActiveProducts, setHasActiveProducts] = useState(null);
  const [options, setOptions] = useState({
    terms: [],
    taxes: [],
    currencies: currencyOptions.map((currency) => ({ value: currency, label: currency })),
    contacts: [],
    billingAddresses: [],
    shippingAddresses: [],
  });
  const [addressDialogTarget, setAddressDialogTarget] = useState(null);
  const [addressDraft, setAddressDraft] = useState(emptyAddressDraft);
  const [addressSaving, setAddressSaving] = useState(false);
  const [addressError, setAddressError] = useState("");

  const documentType = transition?.documentType || "";
  const config = documentConfig[documentType] || { title: "Create Document" };
  const orderId = transition?.card?.orderId;

  useEffect(() => {
    if (!open || !orderId) return;
    let active = true;
    setLoading(true);
    setError("");
    setRetryNotice("");
    setConfirmation(null);
    setCreateNewConfirmed(false);
    setPreviewReady(false);
    setHasActiveProducts(null);
    setOrderDetails(null);
    setAddressDialogTarget(null);
    setAddressDraft(emptyAddressDraft);
    setAddressError("");

    Promise.allSettled([
      retryTransientRequest(
        () => fetchCrmOrderDocumentOptions(instanceUniqueId, orderId, documentType),
        {
        retries: 2,
        onRetry: () => {
          if (active) setRetryNotice(RETRYING_CONNECTIVITY_MESSAGE);
        },
        }
      ),
      retryTransientRequest(
        () => previewCrmOrderTransition(instanceUniqueId, orderId, documentType),
        { retries: 1 }
      ),
    ])
      .then(([optionsResult, previewResult]) => {
        if (!active) return;

        if (optionsResult.status === "fulfilled") {
          const nextOptions = normalizeOptions(optionsResult.value);
          setOptions(nextOptions);
          setOrderDetails(optionsResult.value?.project || null);
          setValues(buildDefaults({
            card: transition?.card,
            response: optionsResult.value,
            documentType,
            options: nextOptions,
          }));
        } else {
          if (isInternalApiError(optionsResult.reason)) {
            console.warn("FastUpdateDialog scoped options load failed:", optionsResult.reason);
          }
          setError(getFriendlyApiError(
            optionsResult.reason,
            "Could not load customer-scoped document options. Please try again."
          ));
        }

        if (previewResult.status === "fulfilled") {
          setConfirmation(previewResult.value?.requiresConfirmation ? previewResult.value : null);
          setHasActiveProducts(previewResult.value?.hasActiveProducts !== false);
          setPreviewReady(true);
        } else {
          if (isInternalApiError(previewResult.reason)) console.warn("FastUpdateDialog duplicate preview failed:", previewResult.reason);
          setError(getFriendlyApiError(previewResult.reason, "Could not check for existing documents. Please try again."));
        }
        setRetryNotice("");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [documentType, instanceUniqueId, open, orderId, transition?.card]);

  const projectLink = useMemo(() => {
    if (!instanceUniqueId || !orderId) return "#";
    return `/instance/${instanceUniqueId}/sales/orders/${orderId}/overview`;
  }, [instanceUniqueId, orderId]);

  const openAddressDialog = (target) => {
    setAddressDialogTarget(target);
    setAddressDraft({
      ...emptyAddressDraft,
      companyName: orderDetails?.customerName || "",
    });
    setAddressError("");
  };

  const closeAddressDialog = () => {
    if (addressSaving) return;
    setAddressDialogTarget(null);
    setAddressDraft(emptyAddressDraft);
    setAddressError("");
  };

  const setAddressField = (field) => (event) => {
    setAddressDraft((current) => ({ ...current, [field]: event.target.value }));
  };

  const saveNewAddress = async () => {
    if (!addressDialogTarget || !orderDetails?.customerId) {
      setAddressError("A valid attached customer is required before adding an address.");
      return;
    }

    setAddressSaving(true);
    setAddressError("");
    try {
      const result = await createCrmOrderCustomerAddress(instanceUniqueId, orderId, {
        customerId: orderDetails.customerId,
        addressType: addressDialogTarget,
        ...addressDraft,
      });
      const address = result?.address;
      if (!address?.id) throw new Error("Address was created but no address ID was returned.");

      const option = {
        ...address,
        value: String(address.id),
      };
      const appendOption = (list) => [
        option,
        ...list.filter((existing) => existing.value !== option.value),
      ];

      setOptions((current) => {
        if (addressDialogTarget === "billing" && values.shippingSameAsBilling) {
          return {
            ...current,
            billingAddresses: appendOption(current.billingAddresses),
            shippingAddresses: appendOption(current.shippingAddresses),
          };
        }
        const optionKey =
          addressDialogTarget === "billing" ? "billingAddresses" : "shippingAddresses";
        return {
          ...current,
          [optionKey]: appendOption(current[optionKey]),
        };
      });
      setValues((current) => {
        const display = option.display || option.label || "";
        if (addressDialogTarget === "billing" && current.shippingSameAsBilling) {
          return {
            ...current,
            billingAddressId: option.value,
            billingAddress: display,
            shippingAddressId: option.value,
            shippingAddress: display,
          };
        }
        const field =
          addressDialogTarget === "billing" ? "billingAddressId" : "shippingAddressId";
        const displayField =
          addressDialogTarget === "billing" ? "billingAddress" : "shippingAddress";
        return { ...current, [field]: option.value, [displayField]: display };
      });
      setAddressDialogTarget(null);
      setAddressDraft(emptyAddressDraft);
      setAddressError("");
    } catch (err) {
      setAddressError(
        err?.response?.data?.message ||
        err?.message ||
        "Could not save the customer address."
      );
    } finally {
      setAddressSaving(false);
    }
  };

  const setField = (field) => (event) => {
    const nextValue = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    setValues((current) => {
      if (field === "commentsEnabled") {
        return { ...current, commentsEnabled: String(nextValue) !== "false" };
      }

      if (field === "shippingSameAsBilling") {
        return nextValue
          ? {
              ...current,
              shippingSameAsBilling: true,
              shippingContactId: current.billingContactId || "",
              shippingContact: current.billingContact || "",
              shippingAddressId: current.billingAddressId || "",
              shippingAddress: current.billingAddress || "",
            }
          : { ...current, shippingSameAsBilling: false };
      }

      if (field === "terms" && documentType === "salesOrder") {
        const policy = options.terms.find((term) => term.value === String(nextValue))?.paymentPolicy || null;
        const requiresPayment = Boolean(policy?.requireUpfrontPayment);
        return {
          ...current,
          terms: nextValue,
          requireUpfrontPayment: requiresPayment,
          upfrontMode: requiresPayment ? policy?.upfrontMode || "full" : null,
          depositType:
            requiresPayment && policy?.upfrontMode === "deposit"
              ? policy?.depositType || "percent"
              : null,
          depositValue:
            requiresPayment && policy?.upfrontMode === "deposit"
              ? policy?.depositValue ?? ""
              : "",
          paymentDueOnSign: requiresPayment ? policy?.paymentDueOnSign ?? true : null,
        };
      }

      if (field === "billingContactId" || field === "shippingContactId") {
        const contact = options.contacts.find((option) => option.value === nextValue);
        const displayField = field === "billingContactId" ? "billingContact" : "shippingContact";
        const next = { ...current, [field]: nextValue, [displayField]: contact?.label || "" };
        if (field === "billingContactId" && current.shippingSameAsBilling) {
          next.shippingContactId = nextValue;
          next.shippingContact = contact?.label || "";
        }
        return next;
      }

      if (field === "billingAddressId" || field === "shippingAddressId") {
        const addressOptions =
          field === "billingAddressId" ? options.billingAddresses : options.shippingAddresses;
        const address = addressOptions.find((option) => option.value === nextValue);
        const displayField = field === "billingAddressId" ? "billingAddress" : "shippingAddress";
        const display = address?.display || address?.label || "";
        const next = { ...current, [field]: nextValue, [displayField]: display };
        if (field === "billingAddressId" && current.shippingSameAsBilling) {
          next.shippingAddressId = nextValue;
          next.shippingAddress = display;
        }
        return next;
      }

      if (field === "clientContactId") {
        const contact = options.contacts.find((option) => option.value === nextValue);
        return {
          ...current,
          clientContactId: nextValue,
          clientContactEmail: contact?.email || "",
        };
      }

      return { ...current, [field]: nextValue };
    });
  };

  const validate = () => {
    if (documentType === "presentation" && !values.presentationDate) return "Presentation date is required.";
    if (documentType === "estimate" && !values.inhandsDate) return "In-hands date is required to create an estimate.";
    if (documentType === "salesOrder" && !values.inhandsDate) return "In-hands date is required to create a sales order.";
    if (!values.inhandsDate) return "In-hands date is required.";
    if (!values.currency) return "Currency is required.";
    if (documentType === "presentation" && !values.clientContactEmail) return "Client contact email is required.";
    return "";
  };

  const buildFields = () => {
    const common = {
      documentTitle: values.documentTitle,
      title: values.documentTitle,
      commentsEnabled: values.commentsEnabled !== false,
      orderDate: values.orderDate,
      terms: values.terms,
      currency: values.currency,
      inhandsDate: values.inhandsDate,
      defaultTax: values.defaultTax,
      billingAddress: values.billingAddress,
      billingAddressId: values.billingAddressId || null,
      billingContact: values.billingContact,
      billingName: values.billingContact,
      billingContactId: values.billingContactId || null,
      shippingAddress: values.shippingAddress,
      shippingAddressId: values.shippingAddressId || null,
      shippingContact: values.shippingContact,
      shippingName: values.shippingContact,
      shippingContactId: values.shippingContactId || null,
    };

    if (documentType === "presentation") {
      return {
        ...common,
        presentationDate: values.presentationDate,
        hidePricing: values.hidePricing === true,
        clientContact: {
          id: values.clientContactId || null,
          email: values.clientContactEmail,
        },
      };
    }

    if (documentType === "salesOrder") {
      return {
        ...common,
        requireUpfrontPayment: values.requireUpfrontPayment === true,
        upfrontMode: values.requireUpfrontPayment ? values.upfrontMode : null,
        depositType: values.requireUpfrontPayment ? values.depositType : null,
        depositValue: values.requireUpfrontPayment ? values.depositValue : null,
        paymentDueOnSign: values.requireUpfrontPayment ? values.paymentDueOnSign === true : null,
      };
    }

    return common;
  };

  const buildTransitionPayload = (options = {}) => ({
    targetLane: transition.targetLane,
    targetStatus: transition.targetStatus,
    documentType,
    fields: buildFields(),
    sendEmail: false,
    beforeOrderId: transition.beforeOrderId || null,
    afterOrderId: transition.afterOrderId || null,
    expectedStatus: transition.sourceStatus,
    expectedStage: transition.sourceLane,
    expectedUpdatedAt: transition.card?.updatedAt || undefined,
    ...(options.confirmCreateNew || createNewConfirmed ? { confirmCreateNew: true } : {}),
    ...(options.useExistingDocumentId ? { useExistingDocumentId: options.useExistingDocumentId, skipDocumentCreation: true } : {}),
  });

  const handleTransitionResult = (result) => {
    if (result?.requiresConfirmation) {
      setConfirmation(result);
      setCreateNewConfirmed(false);
      setError("");
      return;
    }
    onSuccess?.(result);
  };

  const submit = async (options = {}) => {
    if (!options.skipValidation) {
      const validationError = validate();
      if (validationError) {
        setError(validationError);
        return;
      }
    }

    setSubmitting(true);
    setError("");
    setRetryNotice("");
    try {
      const result = await transitionCrmOrder(instanceUniqueId, orderId, buildTransitionPayload(options));
      handleTransitionResult(result);
    } catch (err) {
      if (
        Number(err?.response?.status) === 409 &&
        err?.response?.data?.details?.code === "STALE_CARD_STATE"
      ) {
        await onConflict?.(err);
        return;
      }
      const data = err?.response?.data;
      if (data?.requiresConfirmation) {
        setConfirmation(data);
        setCreateNewConfirmed(false);
        setError("");
        return;
      }
      if (isInternalApiError(err)) console.warn("FastUpdateDialog transition failed:", err);
      setError(getFriendlyApiError(err, "Could not create the document."));
    } finally {
      setSubmitting(false);
    }
  };

  const createNewAnyway = () => {
    setConfirmation(null);
    setCreateNewConfirmed(true);
    setPreviewReady(true);
    setError("");
  };

  const handleUseExistingDocument = (doc) => {
    if (!doc?.id) return;
    submit({ useExistingDocumentId: doc.id, skipValidation: true });
  };

  const openExisting = (route) => {
    openInNewTab(route);
  };

  const openCart = () => {
    if (typeof onOpenCart === "function") {
      try {
        const opened = onOpenCart(transition?.card);
        if (opened !== false) return;
      } catch (_) {}
    }
    openInNewTab(projectLink);
  };

  const renderCommonFields = () => (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="Status" select value={documentType === "estimate" ? "Estimate Draft" : "Agreement Pending"} fullWidth disabled>
          <MenuItem value={documentType === "estimate" ? "Estimate Draft" : "Agreement Pending"}>
            {documentType === "estimate" ? "Estimate Draft" : "Agreement Pending"}
          </MenuItem>
        </TextField>
        <TextField size="small" label="Comments mode" select value={String(values.commentsEnabled !== false)} onChange={setField("commentsEnabled")} fullWidth>
          <MenuItem value="true">Comments Enabled</MenuItem>
          <MenuItem value="false">Comments Disabled</MenuItem>
        </TextField>
        <TextField size="small" label="Order date" type="date" value={values.orderDate || ""} onChange={setField("orderDate")} fullWidth InputLabelProps={{ shrink: true }} />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="In-hands date" type="date" value={values.inhandsDate || ""} onChange={setField("inhandsDate")} fullWidth required InputLabelProps={{ shrink: true }} />
        <TextField size="small" label="Currency" select value={values.currency || ""} onChange={setField("currency")} fullWidth required>
          {options.currencies.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <TextField size="small" label="Terms" select value={values.terms || ""} onChange={setField("terms")} fullWidth>
          <MenuItem value="">Select terms</MenuItem>
          {!options.terms.length ? <MenuItem disabled>No configured terms found</MenuItem> : null}
          {options.terms.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Default tax" select value={values.defaultTax || ""} onChange={setField("defaultTax")} fullWidth>
          <MenuItem value="">Select tax</MenuItem>
          {!options.taxes.length ? <MenuItem disabled>No configured taxes found</MenuItem> : null}
          {options.taxes.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}{option.rate != null ? ` (${Number(option.rate) * 100}%)` : ""}
            </MenuItem>
          ))}
        </TextField>
      </Stack>
      <Box sx={twoColumnGridSx}>
        <TextField size="small" label="Billing contact" select value={values.billingContactId || ""} onChange={setField("billingContactId")} fullWidth sx={truncatedSelectSx}>
          <MenuItem value="">Select billing contact</MenuItem>
          {!options.contacts.length ? <MenuItem disabled>No customer contacts found</MenuItem> : null}
          {options.contacts.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Shipping contact" select value={values.shippingContactId || ""} onChange={setField("shippingContactId")} fullWidth disabled={values.shippingSameAsBilling === true} sx={truncatedSelectSx}>
          <MenuItem value="">Select shipping contact</MenuItem>
          {!options.contacts.length ? <MenuItem disabled>No customer contacts found</MenuItem> : null}
          {options.contacts.map((option) => (
            <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
          ))}
        </TextField>
      </Box>
      <Box
        sx={{
          ...twoColumnGridSx,
          gridTemplateAreas: {
            xs: '"billing" "billingAction" "shipping" "shippingAction"',
            sm: '"billing shipping" "billingAction shippingAction"',
          },
          rowGap: 0.75,
        }}
      >
        <TextField
          size="small"
          label="Billing address"
          select
          value={values.billingAddressId || ""}
          onChange={setField("billingAddressId")}
          fullWidth
          sx={{ ...truncatedSelectSx, gridArea: "billing" }}
        >
          <MenuItem value="">Select billing address</MenuItem>
          {!options.billingAddresses.length ? <MenuItem disabled>No customer addresses found</MenuItem> : null}
          {options.billingAddresses.map((option) => (
            <MenuItem key={option.value} value={option.value} title={`${option.label}: ${option.display}`}>
              {option.label}: {option.display}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          label="Shipping address"
          select
          value={values.shippingAddressId || ""}
          onChange={setField("shippingAddressId")}
          fullWidth
          disabled={values.shippingSameAsBilling === true}
          sx={{ ...truncatedSelectSx, gridArea: "shipping" }}
        >
          <MenuItem value="">Select shipping address</MenuItem>
          {!options.shippingAddresses.length ? <MenuItem disabled>No customer addresses found</MenuItem> : null}
          {options.shippingAddresses.map((option) => (
            <MenuItem key={option.value} value={option.value} title={`${option.label}: ${option.display}`}>
              {option.label}: {option.display}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          onClick={() => openAddressDialog("billing")}
          sx={{
            gridArea: "billingAction",
            justifySelf: "start",
            minWidth: 0,
            whiteSpace: "nowrap",
          }}
        >
          + Add New Billing Address
        </Button>
        <Button
          size="small"
          onClick={() => openAddressDialog("shipping")}
          disabled={values.shippingSameAsBilling === true}
          sx={{
            gridArea: "shippingAction",
            justifySelf: "start",
            minWidth: 0,
            whiteSpace: "nowrap",
          }}
        >
          + Add New Shipping Address
        </Button>
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={values.shippingSameAsBilling === true}
            onChange={setField("shippingSameAsBilling")}
          />
        }
        label="Shipping same as billing"
      />
      <TextField
        size="small"
        label="Title shown on document"
        helperText="Customer-facing title for this estimate or agreement."
        value={values.documentTitle || ""}
        onChange={setField("documentTitle")}
        fullWidth
      />
    </Stack>
  );

  const showCreateForm = previewReady && !confirmation?.requiresConfirmation;
  const existingDocuments = confirmation?.existingDocuments || [];
  const hasOnlyStaleExistingDocuments =
    existingDocuments.length > 0 && existingDocuments.every((doc) => doc.isStale === true);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 900 }}>{config.title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert severity="info">
            This creates or uses the target document only. Skipped stages are not generated.
          </Alert>
          {documentType === "estimate" ? (
            <Alert severity="info">
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>Status: Estimate Draft</Typography>
                <Typography variant="body2">{config.helperText}</Typography>
              </Stack>
            </Alert>
          ) : null}
          {documentType === "presentation" ? (
            <Alert severity="info">{config.helperText}</Alert>
          ) : null}
          {documentType === "salesOrder" ? (
            <Alert severity="info">
              <Stack spacing={0.5}>
                <Typography variant="body2" sx={{ fontWeight: 800 }}>Status: Agreement Pending</Typography>
                <Typography variant="body2">{config.helperText}</Typography>
              </Stack>
            </Alert>
          ) : null}
          {loading ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">Loading project defaults...</Typography>
            </Stack>
          ) : null}
          {retryNotice ? <Alert severity="info">{retryNotice}</Alert> : null}
          {error ? (
            <Alert severity="error">
              <Stack spacing={1}>
                <span>{error}</span>
                {/cart|products/i.test(error) ? (
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Button size="small" variant="contained" onClick={openCart}>
                      Open Cart
                    </Button>
                    <Link href={projectLink} target="_blank" rel="noopener noreferrer">
                      Open Full Project in New Tab
                    </Link>
                  </Stack>
                ) : null}
              </Stack>
            </Alert>
          ) : null}
          {hasActiveProducts === false ? (
            <Alert severity="warning">
              <Stack spacing={1}>
                <span>This document cannot be created because the cart has no active products. Please open the cart and add/restore products first.</span>
                <Button size="small" variant="contained" onClick={openCart} sx={{ alignSelf: "flex-start" }}>
                  Open Cart
                </Button>
              </Stack>
            </Alert>
          ) : null}
          {confirmation?.requiresConfirmation ? (
            <Alert severity="warning">
              <Stack spacing={1.25}>
                <span>{confirmation.message}</span>
                {existingDocuments.map((doc) => (
                  <Stack
                    key={`${doc.type}-${doc.id}`}
                    direction={{ xs: "column", sm: "row" }}
                    spacing={1}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          #{doc.presentationId || doc.estimationId || doc.id}
                        </Typography>
                        {doc.isStale ? (
                          <Chip size="small" color="warning" label="Stale - current cart changed" />
                        ) : null}
                      </Stack>
                      <Typography variant="caption" color="text.secondary">
                        {doc.presentationDate ? `Date: ${dateValue(doc.presentationDate)} ` : ""}
                        {[doc.status ? `Status: ${doc.status}` : null, doc.clientContactEmail || null].filter(Boolean).join(" | ")}
                      </Typography>
                      {doc.isStale ? (
                        <Typography variant="caption" color="warning.dark" display="block">
                          {doc.staleReason || "This document was created from older cart items and does not match the current active cart."}
                        </Typography>
                      ) : null}
                    </Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Tooltip
                        title={
                          doc.isStale
                            ? "Cannot use this document because all products were removed or changed. Create a new document from the current cart."
                            : ""
                        }
                      >
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => handleUseExistingDocument(doc)}
                            disabled={submitting || doc.isStale}
                          >
                            Use Existing & Move
                          </Button>
                        </span>
                      </Tooltip>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openExisting(doc.route)}
                        disabled={!doc.route || submitting}
                        endIcon={<OpenInNewRoundedIcon />}
                      >
                        Open in New Tab
                      </Button>
                    </Stack>
                  </Stack>
                ))}
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" onClick={onClose} disabled={submitting}>
                    Cancel
                  </Button>
                  <Button size="small" variant="contained" color="warning" onClick={createNewAnyway} disabled={submitting || hasActiveProducts === false}>
                    {hasOnlyStaleExistingDocuments && hasActiveProducts
                      ? "Create New From Current Cart"
                      : "Create New Anyway"}
                  </Button>
                </Stack>
              </Stack>
            </Alert>
          ) : null}

          <Box>
            <Typography variant="caption" color="text.secondary">Project</Typography>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              #{orderId} {orderDetails?.projectName || transition?.card?.projectName || ""}
            </Typography>
          </Box>

          {showCreateForm ? (
            <>
              {documentType === "presentation" ? (
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField size="small" label="Presentation date" type="date" value={values.presentationDate || ""} onChange={setField("presentationDate")} fullWidth required InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="In-hands date" type="date" value={values.inhandsDate || ""} onChange={setField("inhandsDate")} fullWidth required InputLabelProps={{ shrink: true }} />
                    <TextField size="small" label="Currency" select value={values.currency || ""} onChange={setField("currency")} fullWidth required>
                      {options.currencies.map((option) => (
                        <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                      ))}
                    </TextField>
                  </Stack>
                  <TextField size="small" label="Client contact" select value={values.clientContactId || ""} onChange={setField("clientContactId")} fullWidth required>
                    <MenuItem value="">Select client contact</MenuItem>
                    {options.contacts.filter((option) => option.email).map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}{option.email ? ` (${option.email})` : ""}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Title" value={values.documentTitle || ""} onChange={setField("documentTitle")} fullWidth />
                  <FormControlLabel control={<Checkbox checked={values.hidePricing === true} onChange={setField("hidePricing")} />} label="Hide pricing" />
                </Stack>
              ) : renderCommonFields()}

              {documentType === "salesOrder" ? (
                <Stack spacing={1.5}>
                  <FormControlLabel
                    control={<Checkbox checked={values.requireUpfrontPayment === true} disabled />}
                    label="Require upfront payment (configured by selected terms)"
                  />
                  {values.requireUpfrontPayment ? (
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                      <TextField size="small" label="Upfront mode" select value={values.upfrontMode || "deposit"} fullWidth disabled SelectProps={{ native: true }}>
                        <option value="deposit">Deposit</option>
                        <option value="full">Full</option>
                      </TextField>
                      <TextField size="small" label="Deposit type" select value={values.depositType || "percent"} fullWidth disabled SelectProps={{ native: true }}>
                        <option value="percent">Percent</option>
                        <option value="fixed">Fixed</option>
                      </TextField>
                      <TextField size="small" label="Deposit value" value={values.depositValue || ""} fullWidth disabled />
                      <FormControlLabel control={<Checkbox checked={values.paymentDueOnSign !== false} disabled />} label="Payment due on client sign" />
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}

              <FormControlLabel disabled control={<Checkbox checked={false} />} label="Send email will be available later." />
            </>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button href={projectLink} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewRoundedIcon />}>
          Open full page in new tab
        </Button>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={() => submit()} disabled={submitting || loading || !showCreateForm || hasActiveProducts === false}>
          {submitting ? "Creating..." : "Create"}
        </Button>
      </DialogActions>
      <Dialog
        open={Boolean(addressDialogTarget)}
        onClose={closeAddressDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Add New {addressDialogTarget === "billing" ? "Billing" : "Shipping"} Address
        </DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 0.5 }}>
            {addressError ? <Alert severity="error">{addressError}</Alert> : null}
            <TextField
              size="small"
              label="Address label"
              value={addressDraft.addressName}
              onChange={setAddressField("addressName")}
              required
              fullWidth
            />
            <TextField
              size="small"
              label="Company / name"
              value={addressDraft.companyName}
              onChange={setAddressField("companyName")}
              fullWidth
            />
            <TextField
              size="small"
              label="Address line 1"
              value={addressDraft.streetAddress}
              onChange={setAddressField("streetAddress")}
              required
              fullWidth
            />
            <TextField
              size="small"
              label="Address line 2"
              value={addressDraft.suiteApt}
              onChange={setAddressField("suiteApt")}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                size="small"
                label="City"
                value={addressDraft.city}
                onChange={setAddressField("city")}
                required
                fullWidth
              />
              <TextField
                size="small"
                label="Province / state"
                value={addressDraft.stateProvince}
                onChange={setAddressField("stateProvince")}
                required
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                size="small"
                label="Postal / ZIP"
                value={addressDraft.postalZipCode}
                onChange={setAddressField("postalZipCode")}
                required
                fullWidth
              />
              <TextField
                size="small"
                label="Country"
                value={addressDraft.country}
                onChange={setAddressField("country")}
                required
                fullWidth
              />
            </Stack>
            <TextField
              size="small"
              label="Address type"
              value={addressDialogTarget === "billing" ? "Billing" : "Shipping"}
              disabled
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddressDialog} disabled={addressSaving}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveNewAddress}
            disabled={
              addressSaving ||
              !addressDraft.addressName.trim() ||
              !addressDraft.streetAddress.trim() ||
              !addressDraft.city.trim() ||
              !addressDraft.stateProvince.trim() ||
              !addressDraft.postalZipCode.trim() ||
              !addressDraft.country.trim()
            }
          >
            {addressSaving ? "Saving..." : "Save Address"}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
}
